import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Box,
  Link,
  Stack,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const OrganizationList = () => {
  const [organizations, setOrganizations] = useState([]);
  const [copiedSessionId, setCopiedSessionId] = useState(null);
  const [copiedReportId, setCopiedReportId] = useState(null);

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          "https://genz-staging.feeltiptop.com/api/social_glass/organizations",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setOrganizations(res.data);
      } catch (err) {
        console.error("Failed to fetch organizations:", err);
      }
    };
    fetchOrgs();
  }, []);

  const copySessionLinkToClipboard = (id, link) => {
    navigator.clipboard.writeText(link);
    setCopiedSessionId(id);
    setTimeout(() => setCopiedSessionId(null), 2000);
  };

  const copyReportLinkToClipboard = (id, link) => {
    navigator.clipboard.writeText(link);
    setCopiedReportId(id);
    setTimeout(() => setCopiedReportId(null), 2000);
  };

  return (
   <Box sx={{ width: "100%", minHeight: "100vh", mt: 5, px: { xs: 1, sm: 2 } }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
      >
        Your Organizations
      </Typography>

      {/* Desktop Table View */}
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <TableContainer
          component={Paper}
          elevation={3}
          sx={{ width: "100%", overflowX: "auto" }}
        >
          <Table sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ borderRight: "1px solid #ccc" }}>
                  <strong>Organization</strong>
                </TableCell>
                <TableCell sx={{ borderRight: "1px solid #ccc" }}>
                  <strong>Social Glass Feed Link</strong>
                </TableCell>
                <TableCell>
                  <strong>Social Glass Report Link</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {organizations.map((org) => (
                <TableRow key={org.OrganizationID} hover>
                  <TableCell sx={{ borderRight: "1px solid #ccc" }}>
                    {org.Name}
                  </TableCell>
                  <TableCell sx={{ borderRight: "1px solid #ccc" }}>
                    <Box display="flex" alignItems="center">
                      <Link
                        href={org.SessionLink}
                        target="_blank"
                        rel="noreferrer"
                        underline="hover"
                        sx={{
                          maxWidth: "100%",
                          overflow: "hidden",
                          wordBreak: "break-all",
                          whiteSpace: "normal",
                          display: "inline-block",
                        }}
                      >
                        {org.SessionLink}
                      </Link>
                      <Tooltip
                        title={
                          copiedSessionId === org.OrganizationID ? "Copied!" : "Copy"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            copySessionLinkToClipboard(
                              org.OrganizationID,
                              org.SessionLink,
                            )
                          }
                          sx={{ ml: 1 }}
                        >
                          {copiedSessionId === org.OrganizationID ? (
                            <CheckCircleOutlineIcon
                              color="success"
                              fontSize="small"
                            />
                          ) : (
                            <ContentCopyIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Link
                        href={org.ReportLink}
                        target="_blank"
                        rel="noreferrer"
                        underline="hover"
                        sx={{
                          maxWidth: "100%",
                          overflow: "hidden",
                          wordBreak: "break-all",
                          whiteSpace: "normal",
                          display: "inline-block",
                        }}
                      >
                        {org.ReportLink}
                      </Link>
                      <Tooltip
                        title={
                          copiedReportId === org.OrganizationID
                            ? "Copied!"
                            : "Copy"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            copyReportLinkToClipboard(
                              org.OrganizationID,
                              org.ReportLink,
                            )
                          }
                          sx={{ ml: 1 }}
                        >
                          {copiedReportId === org.OrganizationID ? (
                            <CheckCircleOutlineIcon
                              color="success"
                              fontSize="small"
                            />
                          ) : (
                            <ContentCopyIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {organizations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No organizations found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Mobile Card View */}
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        {organizations.length === 0 ? (
          <Paper elevation={3} sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              No organizations found.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {organizations.map((org) => (
              <Paper key={org.OrganizationID} elevation={3} sx={{ p: 2 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontSize: "1.1rem", fontWeight: 600 }}
                >
                  {org.Name}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Social Glass Feed Link
                  </Typography>
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <Link
                      href={org.SessionLink}
                      target="_blank"
                      rel="noreferrer"
                      underline="hover"
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        wordBreak: "break-all",
                        flex: 1,
                        lineHeight: 1.4,
                      }}
                    >
                      {org.SessionLink}
                    </Link>
                    <Tooltip
                      title={
                        copiedSessionId === org.OrganizationID
                          ? "Copied!"
                          : "Copy"
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          copySessionLinkToClipboard(
                            org.OrganizationID,
                            org.SessionLink,
                          )
                        }
                        sx={{
                          flexShrink: 0,
                          p: 0.5,
                          mt: -0.5,
                        }}
                      >
                        {copiedSessionId === org.OrganizationID ? (
                          <CheckCircleOutlineIcon
                            color="success"
                            fontSize="small"
                          />
                        ) : (
                          <ContentCopyIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Social Glass Report Link
                  </Typography>
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <Link
                      href={org.ReportLink}
                      target="_blank"
                      rel="noreferrer"
                      underline="hover"
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        wordBreak: "break-all",
                        flex: 1,
                        lineHeight: 1.4,
                      }}
                    >
                      {org.ReportLink}
                    </Link>
                    <Tooltip
                      title={
                        copiedReportId === org.OrganizationID
                          ? "Copied!"
                          : "Copy"
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          copyReportLinkToClipboard(
                            org.OrganizationID,
                            org.ReportLink,
                          )
                        }
                        sx={{
                          flexShrink: 0,
                          p: 0.5,
                          mt: -0.5,
                        }}
                      >
                        {copiedReportId === org.OrganizationID ? (
                          <CheckCircleOutlineIcon
                            color="success"
                            fontSize="small"
                          />
                        ) : (
                          <ContentCopyIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default OrganizationList;
